import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  LazyMotion, domAnimation, MotionConfig, AnimatePresence, m, animate, useReducedMotion,
  type HTMLMotionProps,
} from "framer-motion";
import { DUR, EASE_ENTER, SPRING_TOUCH, stepVariants } from "./tokens";
import { cn } from "@/lib/utils";

export function MotionRoot({ children }: { children: ReactNode }) {
  return (
    <LazyMotion features={domAnimation} strict>
      <MotionConfig reducedMotion="user" transition={{ duration: DUR.ui, ease: EASE_ENTER }}>
        {children}
      </MotionConfig>
    </LazyMotion>
  );
}

export function StepTransition({ stepKey, direction, children }: { stepKey: string; direction: 1 | -1; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const reduce = useReducedMotion();
  const focusHeading = () => ref.current?.querySelector<HTMLElement>("[data-step-heading]")?.focus({ preventScroll: false });
  useEffect(() => { if (reduce) focusHeading(); }, [stepKey, reduce]);
  return (
    <AnimatePresence mode="wait" custom={direction} initial={false}>
      <m.div
        key={stepKey}
        ref={ref}
        custom={direction}
        variants={stepVariants}
        initial={reduce ? { opacity: 0 } : "enter"}
        animate={reduce ? { opacity: 1, transition: { duration: DUR.micro } } : "center"}
        exit={reduce ? { opacity: 0, transition: { duration: DUR.micro } } : "exit"}
        onAnimationComplete={(def) => { if (def === "center") focusHeading(); }}
      >
        {children}
      </m.div>
    </AnimatePresence>
  );
}

export function Pressable({ selected, className, children, ...rest }: Omit<HTMLMotionProps<"button">, "children"> & { selected?: boolean; children?: ReactNode }) {
  return (
    <m.button
      type="button"
      whileTap={{ scale: 0.97 }}
      transition={SPRING_TOUCH}
      aria-pressed={selected}
      className={cn("relative outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary)]", className)}
      {...rest}
    >
      {children}
      {selected && (
        <m.span
          aria-hidden
          className="pointer-events-none absolute inset-0 rounded-[inherit] ring-2 ring-[var(--color-primary)]"
          initial={{ opacity: 0, scale: 1.04 }}
          animate={{ opacity: 1, scale: 1, transition: SPRING_TOUCH }}
        />
      )}
    </m.button>
  );
}

export function CountUp({ value, format }: { value: number; format: (n: number) => string }) {
  const reduce = useReducedMotion();
  const [shown, setShown] = useState(value);
  const [flash, setFlash] = useState(false);
  const prev = useRef(value);
  useEffect(() => {
    if (reduce) { setShown(value); prev.current = value; return; }
    if (prev.current === value) return;
    setFlash(true);
    const controls = animate(prev.current, value, {
      type: "spring", stiffness: 140, damping: 22,
      onUpdate: (v) => setShown(Math.round(v)),
      onComplete: () => setFlash(false),
    });
    prev.current = value;
    return () => controls.stop();
  }, [value, reduce]);
  return (
    <span className={cn("tabular-nums transition-colors duration-300", flash && "text-[var(--color-accent)]")}>
      {format(reduce ? value : shown)}
    </span>
  );
}

export function DrawCheck({ done, size = 18 }: { done: boolean; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden className="shrink-0">
      <circle cx="12" cy="12" r="10" fill="none" stroke="var(--color-border)" strokeWidth="2" />
      <m.circle cx="12" cy="12" r="10" fill="none" stroke="var(--color-primary)" strokeWidth="2"
        initial={false} animate={{ opacity: done ? 1 : 0 }} transition={{ duration: DUR.ui }} />
      <m.path d="M7 12.5l3.2 3.2L17 9" fill="none" stroke="var(--color-primary)" strokeWidth="2.4"
        strokeLinecap="round" strokeLinejoin="round"
        initial={false} animate={{ pathLength: done ? 1 : 0, opacity: done ? 1 : 0 }}
        transition={{ duration: 0.28, ease: EASE_ENTER }} />
    </svg>
  );
}

export function ProgressRing({ value, size = 44, stroke = 4 }: { value: number; size?: number; stroke?: number }) {
  const r = (size - stroke) / 2;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden className="-rotate-90">
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--color-border)" strokeWidth={stroke} />
      <m.circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--color-primary)" strokeWidth={stroke}
        strokeLinecap="round" initial={false}
        animate={{ pathLength: Math.max(0.001, Math.min(1, value)) }}
        transition={{ type: "spring", stiffness: 120, damping: 20 }} />
    </svg>
  );
}

export function Shake({ trigger, children }: { trigger: number; children: ReactNode }) {
  return (
    <m.div
      key={trigger}
      initial={false}
      animate={trigger > 0 ? { x: [0, -4, 4, -4, 4, 0] } : { x: 0 }}
      transition={{ duration: 0.24 }}
    >
      {children}
    </m.div>
  );
}
